// src/pages/CourseTime/components/slides/layouts/HtmlContent/HtmlContentSlide.comp.jsx
import React from 'react';
import * as S from '../../../../styles';
import KeyPointsList from '../../common/KeyPointsList.comp';
import SlideTitleSpoken, { resolveSlideTitle } from '../../common/SlideTitleSpoken.comp';
import { parseInlineMarkdown } from '../../../../utils';
import { useSlideContentRenderer } from '../../../../hooks/useSlideContentRenderer';

// Layout: TemplateHtmlContent (slo_title / key_points / content_new-more /
// generic HTML fallback branches)
const HtmlContentSlide = ({ slide, renderSpokenText, skipTitleSpeech }) => {
  const { renderContent } = useSlideContentRenderer({ renderSpokenText, skipTitleSpeech });

  const origin = slide._pbdq_origin || slide;
  const slideType = origin.slide_type || slide._pbdq_kind;

  if (slideType === 'slo_title') {
    const title = origin.title || origin.slo_title || origin.description_for_learner || '';
    const icon = origin.icon || '';
    const promise = origin.promise_text || origin.promise || '';

    return (
      <S.HtmlContentColumn>
        <S.HtmlContentWrapper>
          <div className="slo-title-card">
            <div className="slo-title-kicker">Learning Objective</div>
            {icon && <div className="slo-title-icon">{icon}</div>}
            {title && <h1 className="slo-title-main">{parseInlineMarkdown(title)}</h1>}
            {promise && <p className="content-body" style={{ textAlign: 'center', margin: 0 }}>{parseInlineMarkdown(promise)}</p>}
          </div>
        </S.HtmlContentWrapper>
      </S.HtmlContentColumn>
    );
  }

  if (slideType === 'key_points') {
    const intro = origin.intro_line || '';
    const closing = origin.closing_line || '';
    const points = origin.points || [];

    return (
      <KeyPointsList
        slide={slide}
        skipTitleSpeech={skipTitleSpeech}
        renderSpokenText={renderSpokenText}
        intro={intro}
        closing={closing}
        points={points}
      />
    );
  }

  if (slideType === 'scenario' || slideType === 'bridge') {
    const scenarioText = origin.scenario_text || origin.story_text || '';
    const dialogue = origin.character_dialogue || origin.dialogue || '';
    const charName = origin.character_name || origin.character || '';

    const renderScenarioContent = () => (
      <>
        {scenarioText && (
          <div className="scenario-text">
            {parseInlineMarkdown(scenarioText)}
          </div>
        )}
        {dialogue && (
          <div className="speech-bubble">
            {charName ? <strong>{charName}: </strong> : null}
            {parseInlineMarkdown(dialogue)}
          </div>
        )}
      </>
    );

    if (slide.Images && slide.Images.length > 0) {
      return (
        <S.HtmlContentLayout>
          <S.HtmlImageContainer>
            <S.ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  WebkitUserDrag: 'none',
                }}
              />
            </S.ImagePlaceholder>
          </S.HtmlImageContainer>
          <S.HtmlContentColumn>
            {resolveSlideTitle(slide).trim() && (
              <S.SlideTitle>
                <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
              </S.SlideTitle>
            )}
            <S.HtmlContentWrapper>
              {renderScenarioContent()}
            </S.HtmlContentWrapper>
          </S.HtmlContentColumn>
        </S.HtmlContentLayout>
      );
    }

    return (
      <S.HtmlContentColumn>
        {resolveSlideTitle(slide).trim() && (
          <S.SlideTitle>
            <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
          </S.SlideTitle>
        )}
        <S.HtmlContentWrapper>
          {renderScenarioContent()}
        </S.HtmlContentWrapper>
      </S.HtmlContentColumn>
    );
  }

  if (slideType === 'reveal') {
    const correctAnswer = origin.correct_answer_text || '';
    const reason = origin.one_sentence_reason || '';
    const followUpAction = origin.follow_up_action || '';

    const renderRevealContent = () => (
      <>
        {(correctAnswer || reason) && (
          <div className="reveal-big">
            {correctAnswer && <div className="answer-label">Correct Answer</div>}
            {correctAnswer && <div className="answer">{parseInlineMarkdown(correctAnswer)}</div>}
            {reason && <div className="reason">{parseInlineMarkdown(reason)}</div>}
          </div>
        )}
        {followUpAction && (
          <div className="content-so-what">
            {parseInlineMarkdown(followUpAction)}
          </div>
        )}
      </>
    );

    if (slide.Images && slide.Images.length > 0) {
      return (
        <S.HtmlContentLayout>
          <S.HtmlImageContainer>
            <S.ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  WebkitUserDrag: 'none',
                }}
              />
            </S.ImagePlaceholder>
          </S.HtmlImageContainer>
          <S.HtmlContentColumn>
            {resolveSlideTitle(slide).trim() && (
              <S.SlideTitle>
                <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
              </S.SlideTitle>
            )}
            <S.HtmlContentWrapper>
              {renderRevealContent()}
            </S.HtmlContentWrapper>
          </S.HtmlContentColumn>
        </S.HtmlContentLayout>
      );
    }

    return (
      <S.HtmlContentColumn>
        {resolveSlideTitle(slide).trim() && (
          <S.SlideTitle>
            <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
          </S.SlideTitle>
        )}
        <S.HtmlContentWrapper>
          {renderRevealContent()}
        </S.HtmlContentWrapper>
      </S.HtmlContentColumn>
    );
  }

  // Closing "summary" slide: its points are slo_recaps (preview: HtmlContentSlide §8).
  if (slideType === 'summary') {
    const recaps = origin.slo_recaps || [];

    return (
      <S.HtmlContentColumn>
        {resolveSlideTitle(slide).trim() && (
          <S.SlideTitle>
            <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
          </S.SlideTitle>
        )}
        <S.HtmlContentWrapper>
          {recaps.length > 0 && (
            <ul className="recap-list">
              {recaps.map((r, idx) => (
                <li key={idx}>{parseInlineMarkdown(r)}</li>
              ))}
            </ul>
          )}
        </S.HtmlContentWrapper>
      </S.HtmlContentColumn>
    );
  }

  // Celebration / badge: as the preview draws it (HtmlContentSlide §7) —
  // the image beside a badge pill, the message and any recaps; no title.
  if (slideType === 'celebration' || slideType === 'badge') {
    const badgeLabel = origin.badge_label || '';
    const celebrationMessage = origin.celebration_message || origin.achievement_line || origin.headline || '';
    const recaps = (origin.slo_recaps && origin.slo_recaps.length > 0)
      ? origin.slo_recaps
      : [origin.slo1_recap, origin.slo2_recap, origin.slo3_recap].filter(Boolean);

    const renderCelebrationContent = () => (
      <div className="celebration-container">
        {badgeLabel && (
          <div className="celebration-badge">
            {parseInlineMarkdown(badgeLabel)}
          </div>
        )}
        {celebrationMessage && (
          <h2 className="celebration-message">
            {parseInlineMarkdown(celebrationMessage)}
          </h2>
        )}
        {recaps.length > 0 && (
          <ul className="celebration-recaps">
            {recaps.map((recap, idx) => (
              <li key={idx}>
                <span>{parseInlineMarkdown(recap)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    if (slide.Images && slide.Images.length > 0) {
      return (
        <S.HtmlContentLayout>
          <S.HtmlImageContainer>
            <S.ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  WebkitUserDrag: 'none',
                }}
              />
            </S.ImagePlaceholder>
          </S.HtmlImageContainer>
          <S.HtmlContentColumn style={{ alignItems: 'center', textAlign: 'center' }}>
            <S.HtmlContentWrapper style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              {renderCelebrationContent()}
            </S.HtmlContentWrapper>
          </S.HtmlContentColumn>
        </S.HtmlContentLayout>
      );
    }

    return (
      <S.HtmlContentColumn style={{ alignItems: 'center', textAlign: 'center' }}>
        <S.HtmlContentWrapper style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          {renderCelebrationContent()}
        </S.HtmlContentWrapper>
      </S.HtmlContentColumn>
    );
  }

  const isSplitTail = slide._split_tail || origin._split_tail;
  // framework / stakes / guide: the preview draws them in this same branch
  // (DQProV2 HtmlContentSlide §10); stakes is the content_more shape.
  if (slideType === 'content_new' || slideType === 'content_more' || slideType === 'content'
      || slideType === 'framework' || slideType === 'stakes' || slideType === 'guide') {
    const isContentMore = slideType === 'content_more' || slideType === 'stakes';
    const hook = isContentMore ? origin.high_stakes_moment : origin.hook;
    const soWhat = origin.so_what;

    const renderSlideMainContent = () => (
      <>
        {/* Hook / High Stakes Moment */}
        {hook && (
          <p className="content-hook">
            {parseInlineMarkdown(hook)}
          </p>
        )}

        {/* Content New layouts */}
        {!isContentMore && !isSplitTail && (
          <>
            {/* Body Paragraphs */}
            {Array.isArray(origin.body_paragraphs) && origin.body_paragraphs.length > 0 && (
              <div className="content-body-paragraphs">
                {origin.body_paragraphs.map((paragraph, idx) => (
                  <p key={idx} className="content-body">{parseInlineMarkdown(paragraph)}</p>
                ))}
              </div>
            )}

            {/* Points */}
            {Array.isArray(origin.points) && origin.points.length > 0 && (
              <ul className="content-points">
                {origin.points.map((point, idx) => (
                  <li key={idx}>
                    {parseInlineMarkdown(typeof point === 'string' ? point : point?.text || point?.action || JSON.stringify(point))}
                  </li>
                ))}
              </ul>
            )}

            {/* Steps */}
            {Array.isArray(origin.steps) && origin.steps.length > 0 && (
              <ol className="content-points">
                {origin.steps.map((step, idx) => (
                  <li key={idx}>
                    {parseInlineMarkdown(typeof step === 'string' ? step : step?.action || step?.text || JSON.stringify(step))}
                  </li>
                ))}
              </ol>
            )}

            {/* Taxonomy Framework */}
            {origin.framework_type === 'taxonomy' && origin.taxonomy && (
              <div className="taxonomy-wrapper">
                {origin.taxonomy.map((item, idx) => (
                  <div key={idx} className="taxonomy-item">
                    <span className="taxonomy-number">{idx + 1}. </span>
                    <span className="taxonomy-title">{parseInlineMarkdown(item.name)}</span>
                    <span className="taxonomy-sep"> — </span>
                    <span className="taxonomy-explanation">{parseInlineMarkdown(item.explanation)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Spectrum Framework */}
            {origin.framework_type === 'spectrum' && origin.spectrum && (
              <div className="spectrum-container">
                <div className="spectrum-grid">
                  {origin.spectrum.wrong_mindset && (
                    <div className="spectrum-card wrong">
                      <div className="spectrum-label wrong">Wrong Mindset</div>
                      <div className="spectrum-value wrong">
                        {parseInlineMarkdown(origin.spectrum.wrong_mindset)}
                      </div>
                    </div>
                  )}
                  {origin.spectrum.right_mindset && (
                    <div className="spectrum-card right">
                      <div className="spectrum-label right">Right Mindset</div>
                      <div className="spectrum-value right">
                        {parseInlineMarkdown(origin.spectrum.right_mindset)}
                      </div>
                    </div>
                  )}
                </div>
                {origin.spectrum.comparison_details && (
                  <div className="spectrum-details">
                    {parseInlineMarkdown(origin.spectrum.comparison_details)}
                  </div>
                )}
              </div>
            )}

            {origin.framework_type === 'sequence' && origin.sequence && (() => {
              const isT7 = origin.tier === 'T7' || slide.tier === 'T7';
              return (
                <div className="sequence-container">
                  {/* Before Steps */}
                  {origin.sequence.filter(step => step.timing === 'Before').length > 0 && (
                    <div className="sequence-card before">
                      <div className="sequence-title before">
                        {isT7 ? '1. Before Operations' : '1. First Steps'}
                      </div>
                      <div className="sequence-steps">
                        {origin.sequence.filter(step => step.timing === 'Before').map((step, idx) => (
                          <div key={idx} className="sequence-step">
                            <span className="sequence-arrow">➔ </span>
                            <span className="sequence-action">{parseInlineMarkdown(step.action)}</span>:{' '}
                            <span className="sequence-explanation">{parseInlineMarkdown(step.explanation)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* After Steps */}
                  {origin.sequence.filter(step => step.timing === 'After').length > 0 && (
                    <div className="sequence-card after">
                      <div className="sequence-title after">
                        {isT7 ? '2. After Operations' : '2. Next Actions'}
                      </div>
                      <div className="sequence-steps">
                        {origin.sequence.filter(step => step.timing === 'After').map((step, idx) => (
                          <div key={idx} className="sequence-step">
                            <span className="sequence-arrow">➔ </span>
                            <span className="sequence-action">{parseInlineMarkdown(step.action)}</span>:{' '}
                            <span className="sequence-explanation">{parseInlineMarkdown(step.explanation)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Watch-Outs Modifier */}
            {origin.modifier_type === 'watch_outs' && origin.watch_outs && (
              <div className="watch-outs-container">
                <div className="watch-outs-title">⚠️ Watch-Outs</div>
                {origin.watch_outs.map((w, idx) => (
                  <div key={idx} className="watch-out-item">
                    • {parseInlineMarkdown(w)}
                  </div>
                ))}
              </div>
            )}

            {/* Negative Space Modifier */}
            {origin.modifier_type === 'negative_space' && origin.negative_space && (
              <div className="negative-space-container">
                🚫 <strong>What this is NOT:</strong> {parseInlineMarkdown(origin.negative_space)}
              </div>
            )}

            {/* Relatable Example (Picture this) */}
            {origin.relatable_example && (
              <div className="info-box">
                <span className="label">Picture this:</span>
                <p>"{parseInlineMarkdown(origin.relatable_example)}"</p>
              </div>
            )}
          </>
        )}

        {/* Content More layouts */}
        {isContentMore && !isSplitTail && (
          <>
            {/* Narrative Arc (Gap, Buffer, Collapse) */}
            {origin.narrative_arc === 'the_gap' && (() => {
              const isT7 = origin.tier === 'T7' || slide.tier === 'T7';
              return (
                <div className="narrative-grid gap-arc">
                  <div className="narrative-card old-workflow">
                    <div className="narrative-header old-workflow">
                      <span className="narrative-icon old-workflow">✕</span>
                      <span className="narrative-title old-workflow">
                        {isT7 ? 'Old Workflow' : 'Before'}
                      </span>
                    </div>
                    <p className="narrative-text old-workflow">
                      {parseInlineMarkdown(origin.old_workflow)}
                    </p>
                  </div>
                  <div className="narrative-card new-workflow">
                    <div className="narrative-header new-workflow">
                      <span className="narrative-icon new-workflow">✓</span>
                      <span className="narrative-title new-workflow">
                        {isT7 ? 'New Workflow' : 'After'}
                      </span>
                    </div>
                    <p className="narrative-text new-workflow">
                      {parseInlineMarkdown(origin.new_workflow)}
                    </p>
                  </div>
                </div>
              );
            })()}

            {origin.narrative_arc === 'the_buffer' && (() => {
              const isT7 = origin.tier === 'T7' || slide.tier === 'T7';
              return (
                <div className="narrative-grid buffer-arc">
                  <div className="narrative-card tempting-shortcut">
                    <div className="narrative-header tempting-shortcut">
                      <span className="narrative-icon tempting-shortcut">⚠️</span>
                      <span className="narrative-title tempting-shortcut">
                        {isT7 ? 'Tempting Shortcut' : 'The Shortcut'}
                      </span>
                    </div>
                    <p className="narrative-text tempting-shortcut">
                      {parseInlineMarkdown(origin.tempting_shortcut)}
                    </p>
                  </div>
                  <div className="narrative-card safety-buffer">
                    <div className="narrative-header safety-buffer">
                      <span className="narrative-icon safety-buffer">🛡️</span>
                      <span className="narrative-title safety-buffer">
                        {isT7 ? 'Safety Buffer' : 'Thoughtful Response'}
                      </span>
                    </div>
                    <p className="narrative-text safety-buffer">
                      {parseInlineMarkdown(origin.judgment_intervention)}
                    </p>
                  </div>
                </div>
              );
            })()}

            {origin.narrative_arc === 'the_collapse' && (
              <div className="narrative-collapse-container">
                <div className="narrative-collapse-header">
                  <span className="narrative-collapse-icon warning">⚠️</span>
                  <div className="narrative-collapse-header-text">
                    <div className="narrative-collapse-title warning">Shortcut Taken</div>
                    <div className="narrative-collapse-desc">
                      {parseInlineMarkdown(origin.reasonable_shortcut)}
                    </div>
                  </div>
                </div>
                <div className="narrative-collapse-body">
                  <span className="narrative-collapse-icon crash">💥</span>
                  <div className="narrative-collapse-body-text">
                    <div className="narrative-collapse-title crash">Catastrophic Outcome</div>
                    <div className="narrative-collapse-desc crash">
                      {parseInlineMarkdown(origin.catastrophic_outcome)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Downstream Outcomes */}
            {origin.business_outcomes && (
              <div className="downstream-outcome">
                <strong>Downstream Outcome:</strong> {parseInlineMarkdown(origin.business_outcomes)}
              </div>
            )}

            {/* Final Warning */}
            {origin.final_warning && (
              <div className="final-warning-container">
                💡 {parseInlineMarkdown(origin.final_warning.replace(/^💡\s*/, '').replace(/^final\s+warning\s*[:-]?\s*/i, ''))}
              </div>
            )}
          </>
        )}

        {/* Split Tail (relatable example and so what) */}
        {isSplitTail && (
          <>
            {origin.relatable_example && (
              <div className="info-box">
                <span className="label">Picture this:</span>
                <p>"{parseInlineMarkdown(origin.relatable_example)}"</p>
              </div>
            )}
            {soWhat && (
              <div className="content-so-what">
                {parseInlineMarkdown(soWhat)}
              </div>
            )}
          </>
        )}

        {/* So What (only if not split tail and soWhat is present) */}
        {!isSplitTail && soWhat && (
          <div className="content-so-what">
            {parseInlineMarkdown(soWhat)}
          </div>
        )}
      </>
    );

    if (slide.Images && slide.Images.length > 0) {
      return (
        <S.HtmlContentLayout>
          <S.HtmlImageContainer>
            <S.ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  WebkitUserDrag: 'none',
                }}
              />
            </S.ImagePlaceholder>
          </S.HtmlImageContainer>
          <S.HtmlContentColumn>
            {resolveSlideTitle(slide).trim() && (
              <S.SlideTitle>
                <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
              </S.SlideTitle>
            )}
            <S.HtmlContentWrapper>
              {renderSlideMainContent()}
            </S.HtmlContentWrapper>
          </S.HtmlContentColumn>
        </S.HtmlContentLayout>
      );
    } else {
      return (
        <S.HtmlContentColumn>
          {resolveSlideTitle(slide).trim() && (
            <S.SlideTitle>
              <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
            </S.SlideTitle>
          )}
          <S.HtmlContentWrapper>
            {renderSlideMainContent()}
          </S.HtmlContentWrapper>
        </S.HtmlContentColumn>
      );
    }
  }

  // Fallback for older HTML/CSS content slides
  const htmlContent = typeof slide.SlideContent === 'string' 
    ? slide.SlideContent 
    : Array.isArray(slide.SlideContent) 
      ? slide.SlideContent.join('') 
      : String(slide.SlideContent || '');

  if (slide.Images && slide.Images.length > 0) {
    return (
      <S.HtmlContentLayout>
        <S.HtmlImageContainer>
          <S.ImagePlaceholder>
            <img
              src={slide.Images[0]}
              alt="Slide image"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '4px',
                pointerEvents: 'none',
                WebkitUserDrag: 'none',
              }}
            />
          </S.ImagePlaceholder>
        </S.HtmlImageContainer>
        <S.HtmlContentColumn>
          {resolveSlideTitle(slide).trim() && (
            <S.SlideTitle>
              <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
            </S.SlideTitle>
          )}
          <S.HtmlContentWrapper>
            {renderContent('html_content', htmlContent)}
          </S.HtmlContentWrapper>
        </S.HtmlContentColumn>
      </S.HtmlContentLayout>
    );
  } else {
    return (
      <S.HtmlContentColumn>
        {resolveSlideTitle(slide).trim() && (
          <S.SlideTitle>
            <SlideTitleSpoken slide={slide} skipTitleSpeech={skipTitleSpeech} renderSpokenText={renderSpokenText} />
          </S.SlideTitle>
        )}
        <S.HtmlContentWrapper>
          {renderContent('html_content', htmlContent)}
        </S.HtmlContentWrapper>
      </S.HtmlContentColumn>
    );
  }
};

export default HtmlContentSlide;
